import {
  ACCOUNT_LINK_TOKEN_PATTERN,
  PASSWORD_MIN_LENGTH,
  type AccountLinkInfo,
} from '@bartizo/shared';
import { useEffect, useState, type ReactNode, type SyntheticEvent } from 'react';
import { isApiError } from '../api/client.js';
import { inspectAccountLink, spendAccountLink } from '../api/users.js';
import { useSession } from '../auth/session-context.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { TextField } from '../ui/Field.js';
import { ShieldIcon } from '../ui/icons.js';

/**
 * An invitation or a password reset, from a link an admin made (P8.4).
 *
 * As on the setup page, the token is the part after `#`, which a browser never
 * sends: it is read here and posted, so it reaches no request line and no log. The
 * page first asks what the link is for, so the person sees the address they are
 * choosing a password for. An invitation signs them in; a reset sends them to sign
 * in with the new password (and their authenticator code, if they have one).
 */

const tokenFromLocation = (): string | null => {
  const token = window.location.hash.replace(/^#/, '');
  return ACCOUNT_LINK_TOKEN_PATTERN.test(token) ? token : null;
};

type Looked =
  | { state: 'loading' }
  | { state: 'invalid'; message: string }
  | { state: 'ok'; info: AccountLinkInfo };

export function AccountLinkPage(): ReactNode {
  const { applySession } = useSession();
  const [token] = useState(tokenFromLocation);
  const [looked, setLooked] = useState<Looked>({ state: 'loading' });
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetDone, setResetDone] = useState<string | null>(null);

  useEffect(() => {
    if (token === null) return;
    let live = true;
    inspectAccountLink(token).then(
      (info) => {
        if (live) setLooked({ state: 'ok', info });
      },
      (caught: unknown) => {
        if (live) {
          setLooked({
            state: 'invalid',
            message: isApiError(caught) ? caught.message : 'Could not reach the Bartizo API.',
          });
        }
      },
    );
    return () => {
      live = false;
    };
  }, [token]);

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    if (token === null) return;
    if (password !== repeat) {
      setError('The two passwords are not the same.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const spent = await spendAccountLink(token, password);
      // Spent: it should not stay in the address bar or the history.
      window.history.replaceState(null, '', '/');
      if (spent.session === null) {
        setResetDone(spent.email);
        setBusy(false);
      } else {
        applySession(spent.session);
      }
    } catch (caught: unknown) {
      setError(isApiError(caught) ? caught.message : 'Could not reach the Bartizo API.');
      setBusy(false);
    }
  };

  const invite = looked.state === 'ok' && looked.info.purpose === 'invite';

  return (
    <main className="centred">
      <div className="card" style={{ width: 'min(26rem, 100%)' }}>
        <div className="card-body">
          <div className="stack" style={{ gap: 'var(--space-1)', marginBottom: 'var(--space-2)' }}>
            <ShieldIcon style={{ width: '2rem', height: '2rem', color: 'var(--accent)' }} />
            <h1>{invite ? 'Join Bartizo' : 'Choose a new password'}</h1>
          </div>

          {resetDone !== null ? (
            <Alert tone="success" title="Your password is changed">
              Every session the account had is signed out. <a href="/">Sign in</a> as {resetDone}{' '}
              with the new password.
            </Alert>
          ) : token === null ? (
            <Alert tone="warning" title="This page needs the whole link">
              Open the whole link you were given, including the part after #. If it is lost, ask an
              administrator for a new one.
            </Alert>
          ) : looked.state === 'loading' ? (
            <p className="row muted" aria-busy="true">
              <span className="spinner" aria-hidden="true" />
              Checking the link…
            </p>
          ) : looked.state === 'invalid' ? (
            <Alert tone="warning" title="This link does not work">
              {looked.message}
            </Alert>
          ) : (
            <form onSubmit={(event) => void submit(event)} className="stack">
              <p className="muted text-sm">
                {invite
                  ? `You have been invited as ${looked.info.email}. Choose a password to finish.`
                  : `A new password for ${looked.info.email}.`}
              </p>
              <TextField
                label="Password"
                hint={`At least ${String(PASSWORD_MIN_LENGTH)} characters.`}
                type="password"
                autoComplete="new-password"
                autoFocus
                required
                minLength={PASSWORD_MIN_LENGTH}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
              <TextField
                label="Password again"
                type="password"
                autoComplete="new-password"
                required
                minLength={PASSWORD_MIN_LENGTH}
                value={repeat}
                onChange={(event) => {
                  setRepeat(event.target.value);
                }}
              />

              {error !== null && <Alert tone="danger">{error}</Alert>}

              <Button type="submit" variant="primary" block busy={busy} busyLabel="Saving…">
                {invite ? 'Create my account' : 'Set the new password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
